// Los formatos personalizados de la marca activa, cargados UNA vez y compartidos —
// ADR-2026-10-20-formatos-personalizados.
//
// El selector de formato se monta en muchos lugares a la vez (cada card del chat, Crear,
// los modos): con `useBrandProfile` en cada uno serían varias consultas por instancia. Acá
// se resuelve la marca activa una vez, se leen sus formatos (`descarga_config`, el mismo
// cache de las descargas) y se llena el registro de `formatoPersonalizado`, que leen las
// funciones que arman el pedido. Se recarga al cambiar de marca.

import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import {
  fetchConfigDeDescarga,
  guardarConfigDeDescarga,
  invalidarCacheDeDescarga,
  leerConfigDeDescargaParaEscribir,
  type PerfilDeDescarga,
} from './configDeDescarga';
import { registrarFormatosPersonalizados } from './formatoPersonalizado';
import { resolveMarcaActivaId } from './marcaActiva';

interface Estado {
  brandId: string | null;
  formatos: PerfilDeDescarga[];
  cargado: boolean;
}

const VACIO: Estado = { brandId: null, formatos: [], cargado: false };
let estado: Estado = VACIO;
let cargando: Promise<void> | null = null;
/** Sube con cada cambio de marca: una carga que arrancó antes no publica lo de la marca vieja
 *  encima de la nueva (ADR-2026-10-21-revision-greptile-196-formatos-personalizados). */
let vuelta = 0;
const oyentes = new Set<() => void>();

function publicar(siguiente: Estado) {
  estado = siguiente;
  registrarFormatosPersonalizados(siguiente.formatos);
  for (const o of oyentes) o();
}

/** La marca activa AHORA, resuelta de la base (quién soy → mi empresa → su marca activa).
 *  `null` sin usuario, sin empresa o sin marcas; nunca tira. */
async function resolverMarcaActual(): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: perfil } = await supabase.from('profiles').select('id_company').eq('id', user.id).maybeSingle();
    const companyId = (perfil as { id_company?: string | null } | null)?.id_company ?? null;
    return companyId ? await resolveMarcaActivaId(companyId) : null;
  } catch {
    return null;
  }
}

async function cargar(): Promise<void> {
  const mia = vuelta;
  const publicarSiSigue = (s: Estado) => {
    if (mia === vuelta) publicar(s);
  };
  try {
    const brandId = await resolverMarcaActual();
    const config = brandId ? await fetchConfigDeDescarga(brandId) : null;
    publicarSiSigue({ brandId, formatos: config?.perfiles ?? [], cargado: true });
  } catch {
    publicarSiSigue({ brandId: null, formatos: [], cargado: true });
  }
}

/** Arranca la carga si todavía no se hizo. Idempotente. */
export function asegurarFormatosPersonalizados(): Promise<void> {
  if (!cargando) cargando = cargar();
  return cargando;
}

if (typeof window !== 'undefined') {
  // Funciones avanzadas (o este mismo store) guardó la lista de la marca.
  window.addEventListener('atlas:descarga-config-guardada', (e) => {
    const d = (e as CustomEvent<{ brandId: string; config: { perfiles: PerfilDeDescarga[] } }>).detail;
    if (d?.brandId && d.brandId === estado.brandId) publicar({ ...estado, formatos: d.config.perfiles });
  });
  window.addEventListener('atlas:brand-changed', () => {
    invalidarCacheDeDescarga();
    vuelta++;
    cargando = null;
    // Hasta que llegue la nueva, no hay marca: nadie guarda un formato en la que se fue.
    publicar(VACIO);
    if (oyentes.size > 0) void asegurarFormatosPersonalizados();
  });
}

/** El mismo formato: mismo nombre, misma medida, mismo archivo y mismo peso máximo. El
 *  nombre cuenta: dos tiendas pueden pedir la misma medida y querer un formato cada una. */
const esElMismo = (a: PerfilDeDescarga, b: PerfilDeDescarga): boolean =>
  a.nombre.trim().toLowerCase() === b.nombre.trim().toLowerCase() &&
  a.ancho === b.ancho &&
  a.alto === b.alto &&
  a.formato === b.formato &&
  (a.pesoMaxKB ?? null) === (b.pesoMaxKB ?? null);

/** Las escrituras van de a una: cada una espera a la anterior (ver `agregarFormatoPersonalizado`). */
let cola: Promise<unknown> = Promise.resolve();

/**
 * Después de escribir, el store se vuelve a cargar de cero. Una carga que estaba en vuelo
 * (típico: la persona cambió de marca y el store se vació) había leído la lista ANTES de la
 * escritura; sin esto la publicaba al terminar y el formato recién creado desaparecía del
 * registro, con lo que generar con su slug fallaba (revisión de Greptile al PR #197, cuarta
 * vuelta). Subir `vuelta` la invalida; la carga nueva lee la caché, que ya tiene lo guardado
 * (`fetchConfigDeDescarga` no deja que una lectura vieja la pise). Publica lo de la marca que
 * esté activa: si es otra que la del formato, el formato no aparece, que es lo correcto.
 */
async function recargarDespuesDeEscribir(): Promise<void> {
  vuelta++;
  cargando = null;
  await asegurarFormatosPersonalizados();
}

/**
 * Suma un formato a la marca activa y lo deja listo para elegir. Se guarda en la misma
 * lista que "Formato de descarga" de Funciones avanzadas: es el mismo formato. Si la marca
 * no tenía ninguno, la config nace APAGADA (`activo: false`): eso hace que la hoja de
 * descarga siga sin preguntar en las OTRAS imágenes; las que se generen con este formato
 * se convierten igual, porque llevan la copia en su fila.
 *
 * Es leer la lista entera y volver a guardarla, así que las escrituras van EN SERIE: el
 * agente puede crear dos formatos en una misma respuesta y el chat ejecuta sus herramientas
 * en paralelo; sin la cola, las dos leían la misma lista y la última pisaba a la primera
 * (revisión de Greptile al PR #197). Y es idempotente: si la lista recién leída ya tiene el
 * mismo formato (nombre, medida, archivo y peso), devuelve ése con `existente: true` en vez
 * de duplicarlo — es lo que pasa cuando las dos llamadas paralelas piden lo mismo.
 */
export function agregarFormatoPersonalizado(
  formato: PerfilDeDescarga,
): Promise<{ ok: boolean; error?: string; formato?: PerfilDeDescarga; existente?: boolean }> {
  // La marca se decide AL PEDIR, no cuando llega el turno en la cola: si el usuario cambia de
  // marca mientras una escritura anterior está en vuelo, lo pedido para la marca A se guarda
  // en A igual, y el store sólo se actualiza si A sigue siendo la activa (revisión de Greptile
  // al PR #197, segunda vuelta). Si el store todavía no cargó (o un cambio de marca lo acaba de
  // vaciar), no se espera a esa carga —terminaría con la marca que quede activa DESPUÉS— sino
  // que la marca se resuelve de la base en este momento (tercera vuelta).
  const marcaPedida: Promise<string | null> =
    estado.cargado && estado.brandId ? Promise.resolve(estado.brandId) : resolverMarcaActual();
  const trabajo = cola.then(async () => {
    const brandId = await marcaPedida;
    if (!brandId) return { ok: false, error: 'Todavía no hay una marca para guardar el formato.' };
    // Se relee de la base: otra pestaña (o Funciones avanzadas) pudo cambiar la lista. Si no se
    // pudo leer, no se guarda: escribir sobre un "no hay" falso borraba los formatos que había.
    invalidarCacheDeDescarga();
    const leida = await leerConfigDeDescargaParaEscribir(brandId);
    if (!leida.ok) return { ok: false, error: 'No pudimos leer los formatos de la marca. Probá de nuevo.' };
    const actual = leida.config;
    const igual = actual?.perfiles.find((p) => esElMismo(p, formato));
    if (igual) {
      await recargarDespuesDeEscribir();
      return { ok: true, formato: igual, existente: true };
    }
    const siguiente = actual
      ? { ...actual, perfiles: [...actual.perfiles, formato] }
      : { version: 2 as const, activo: false, siempre: null, perfiles: [formato] };
    const r = await guardarConfigDeDescarga(brandId, siguiente);
    if (!r.ok) return r;
    await recargarDespuesDeEscribir();
    return { ok: true, formato };
  });
  // La cola nunca se corta: un fallo de ESTA escritura no traba a la siguiente.
  cola = trabajo.catch(() => undefined);
  return trabajo;
}

/** Los formatos de la marca activa, cargados: para el prompt del agente y su herramienta. */
export async function formatosPersonalizadosActuales(): Promise<PerfilDeDescarga[]> {
  await asegurarFormatosPersonalizados();
  return estado.formatos;
}

const suscribir = (o: () => void) => {
  oyentes.add(o);
  return () => void oyentes.delete(o);
};

/** Los formatos personalizados de la marca activa, para los selectores. */
export function useFormatosPersonalizados(): Estado {
  useEffect(() => {
    void asegurarFormatosPersonalizados();
  }, []);
  return useSyncExternalStore(suscribir, () => estado, () => estado);
}
